/**
 * "/admin" — the workspace settings: who owns a board once it reaches Shooting
 * (F-133), and which model each Kola AI function runs on.
 * Gated client-side on `role: "admin"` (UX only — the API 403s regardless;
 * see core/useMe.ts and packages/server/src/api/admin-ai-settings.ts).
 *
 * Two independent knobs per AI function:
 *  - "Apply only me" — an in-memory test override for the CALLING admin
 *    only. Lost on a server restart; that is the point (see server module
 *    note on ai/model-settings.ts).
 *  - "Apply to everyone" — persisted, affects every user's model-less runs.
 *    Confirmed via Modal since the blast radius is the whole app.
 *
 * The model each card offers is picked from the SAME dropdown the rest of the
 * app uses (features/ai-models), reading the same admin-managed list edited in
 * the AI models section above — so "which models can I use here?" has one
 * answer everywhere, including here. Reaching a model that is not on the list
 * means adding it to the list first, which is where free text now lives.
 */
import {
  AI_FUNCTIONS,
  type AiFunctionKey,
  type AiModelKind,
  type AiModelSettingResponse,
} from '@kola/shared';
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import {
  clearGlobalAiModel,
  clearPersonalAiModel,
  fetchAiModelSettings,
  setGlobalAiModel,
  setPersonalAiModel,
} from '../../core/api.js';
import { useMe } from '../../core/useMe.js';
import { Modal } from '../../ui/Modal.js';
import { ModelSelect } from '../ai-models/ModelSelect.js';
import { loadAiModels } from '../ai-models/load.js';
import { AiModelsSection } from './AiModelsSection.js';
import { DebugReportsSection } from './DebugReportsSection.js';
import { PresenterPhotosSection } from './PresenterPhotosSection.js';
import { ShootingOwnersSection } from './ShootingOwnersSection.js';
import { ShortsCrewSection } from './ShortsCrewSection.js';
import { TextKindsSection } from './TextKindsSection.js';
import { setDocumentTitle } from '../../core/host.js';
import { ThemeToggle } from '../../ui/ThemeToggle.js';
import { ToastHost, showToast } from '../../ui/toast.js';

/**
 * Which half of the model list each function's picker offers. Getting this
 * wrong is the one model mistake with no recoverable failure mode: an image
 * model on a text function replies with nothing usable, and a text model on
 * an image function fails every frame of a run.
 */
const FUNCTION_INFO: Record<AiFunctionKey, { title: string; hint: string; kind: AiModelKind }> = {
  'translate-column': {
    title: 'Translate Column',
    hint: 'F-70 — translating the Original Script column into a target language.',
    kind: 'text',
  },
  'split-sentence': {
    title: 'Arrange / Split Sentence',
    hint: 'F-60 — suggesting where to cut an over-length, unpunctuated cell.',
    kind: 'text',
  },
  'design-art-direction': {
    title: 'Design Theme — Art Direction',
    hint: "Writing the video's art direction from its script. Creative, not extractive — a stronger model earns its cost here more than elsewhere.",
    kind: 'text',
  },
  'design-image': {
    title: 'Design Theme — Key Visuals',
    hint: 'Rendering key visuals from the art direction. An IMAGE model (e.g. google/gemini-2.5-flash-image) — a text model id here will fail every run. By far the most expensive function.',
    kind: 'image',
  },
  'script-brainstorm': {
    title: 'Script — Ask AI',
    hint: "Answering a writer's question about a highlighted passage, with the whole script as context. Defaults to openrouter/auto — the question is free text, so the router picks a model per question. Pin a specific model here to make cost predictable.",
    kind: 'text',
  },
  'divide-sections': {
    title: 'Divide into Sections',
    hint: 'Grouping the Original Script rows into titled sections, each with a mood. Extractive classification, not creative writing — the cheap default is usually enough. Users can still pick a model per run in the panel.',
    kind: 'text',
  },
  'thumbnail-concepts': {
    title: 'Thumbnails — Hooks',
    hint: "Writing the five hook phrases and titles (Traditional Chinese, Hong Kong) a thumbnail run is built from. Creative work on a whole-script sample — a strong model earns its cost here. The panel's own pick overrides this per board.",
    kind: 'text',
  },
  'thumbnail-image': {
    title: 'Thumbnails — Frames',
    hint: "Rendering the thumbnails from the portraits, the hooks and the board's design theme. An IMAGE model with reference-image support (e.g. google/gemini-3-pro-image) — a text model id here fails every frame. The panel's own pick overrides this per board.",
    kind: 'image',
  },
  'board-name': {
    title: 'Board name from a file',
    hint: 'F-147 — the few English keywords a board created from an imported file is named after (2026Oct08_Ramen Shop Review). One tiny call per file picked; any cheap text model will do.',
    kind: 'text',
  },
  'import-beats': {
    title: 'File import — Split into beats',
    hint: "F-149 step 2 — cutting each row of an imported script into beats (a few words or one short sentence). One text, extractive: a cheap, fast model handles it; each import's panel shows how many rows it cut and how many it had to keep whole.",
    kind: 'text',
  },
  'import-align': {
    title: 'File import — Align columns',
    hint: "F-149 step 4 — dividing each row's visual, sound, extra and translation text across its beats. Reads two languages side by side, so it defaults to the agent's model; the import panel shows how many cells it divided and how many stayed on the first beat row.",
    kind: 'text',
  },
  'board-agent': {
    title: 'Board agent',
    hint: 'The chat panel that reads the open sheet and attached files and proposes edits as a reviewable, revertable plan. Needs a model with FUNCTION CALLING (Gemini, GPT and Claude models do); the auto router does not promise it. An agentic planner earns a stronger model than the translate default — users can still pick one per message in the panel.',
    kind: 'text',
  },
  'portrait-tagging': {
    title: 'Presenter photos — Tagging',
    hint: "Describing each presenter photo's expression for the library below — one call per photo, once. Needs a text model that takes IMAGE INPUT (Gemini, GPT and Claude models do); a text-only model fails every photo. The library's own picker overrides this per sitting.",
    kind: 'text',
  },
  'youtube-description': {
    title: 'YouTube description',
    hint: "F-152 — the description the desktop app's YouTube upload page writes with AI: a hook paragraph and 3–5 key points in Traditional Chinese (Hong Kong), from the script. One call per press; the uploader edits it before anything goes out.",
    kind: 'text',
  },
  'social-caption': {
    title: 'Social post captions (phone)',
    hint: "The Rainvic phone app's Social post page writes captions through this server (the desktop uses the user's own key): the owner's Cantonese SEO persona drafts the shared caption from a brief and adapts it per platform under its playbook rule. One call per press; the poster edits before anything is sent to Buffer.",
    kind: 'text',
  },
};
/**
 * Display order — the canonical AI_FUNCTIONS order, so a new function key
 * gets its row here for free (and the page never depends on whatever order
 * the API happens to return).
 */
const FUNCTION_ORDER: readonly AiFunctionKey[] = AI_FUNCTIONS;

export function AdminScreen() {
  const { me, loading } = useMe();
  if (loading) return null;
  if (me === null || me.role !== 'admin') return <Navigate to="/" replace />;
  return <AdminSettings />;
}

function AdminSettings() {
  const [settings, setSettings] = useState<AiModelSettingResponse[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The pending "apply to everyone" confirmation, if one is open. */
  const [confirming, setConfirming] = useState<{
    functionKey: AiFunctionKey;
    model: string;
  } | null>(null);

  const refresh = async () => {
    try {
      setSettings(await fetchAiModelSettings());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  // The list every picker on this page (and in the app) renders. Refreshed
  // here too, so an admin who edits it below sees the cards' dropdowns follow.
  useEffect(() => {
    void loadAiModels();
  }, []);

  useEffect(() => {
    setDocumentTitle('Kola — Admin');
  }, []);

  const settingFor = (functionKey: AiFunctionKey): AiModelSettingResponse =>
    settings?.find((s) => s.functionKey === functionKey) ?? {
      functionKey,
      effectiveModel: '…',
    };

  return (
    // The outer box is the page's scroller: inside the desktop app Kola's host
    // clips (overflow: clip), so nothing scrolls unless the screen does it.
    <div className="admin-screen">
      <div className="dashboard">
        <div className="dashboard-header">
          <h1>Admin</h1>
          <div className="spacer" />
          <Link to="/" className="btn btn-ghost">
            Back to boards
          </Link>
          <ThemeToggle />
        </div>

        {error !== null && <div className="error-note">{error}</div>}

        {settings === null && error === null && <div className="empty-state">Loading…</div>}

        {/* F-133 first, deliberately: it is a PIPELINE setting — who a board is
            handed to when its storyboard is approved — and the person who comes
            here for it should not have to scroll past the model config. */}
        <ShootingOwnersSection />

        {/* F-142: the standing Shorts crew — the other pipeline setting. */}
        <ShortsCrewSection />

        <AiModelsSection />

        <div className="admin-section-head">
          <h2>AI model defaults</h2>
          <span className="admin-card-hint">
            Which model each Kola AI function runs on when nobody picks one. Choose from the list
            above — "Apply only me" is a test override for you; "Apply to everyone" persists.
          </span>
        </div>

        {settings !== null && (
          <div className="admin-card-list">
            {FUNCTION_ORDER.map((functionKey) => (
              <FunctionCard
                key={functionKey}
                setting={settingFor(functionKey)}
                onApplyOnlyMe={async (model) => {
                  try {
                    await setPersonalAiModel(functionKey, model);
                    showToast(
                      `Testing "${model}" on ${FUNCTION_INFO[functionKey].title} — only for you`,
                    );
                    await refresh();
                  } catch (err) {
                    showToast(
                      err instanceof Error ? err.message : 'Failed to set your test override',
                    );
                  }
                }}
                onClearOnlyMe={async () => {
                  try {
                    await clearPersonalAiModel(functionKey);
                    showToast(`Stopped testing on ${FUNCTION_INFO[functionKey].title}`);
                    await refresh();
                  } catch (err) {
                    showToast(
                      err instanceof Error ? err.message : 'Failed to clear your test override',
                    );
                  }
                }}
                onRequestApplyToEveryone={(model) => setConfirming({ functionKey, model })}
                onClearGlobal={async () => {
                  try {
                    await clearGlobalAiModel(functionKey);
                    showToast(`Reverted ${FUNCTION_INFO[functionKey].title} to the server default`);
                    await refresh();
                  } catch (err) {
                    showToast(
                      err instanceof Error ? err.message : 'Failed to clear the shared default',
                    );
                  }
                }}
              />
            ))}
          </div>
        )}

        <PresenterPhotosSection />

        <TextKindsSection />

        <DebugReportsSection />

        {confirming !== null && (
          <Modal title="Apply to everyone?" onClose={() => setConfirming(null)}>
            <p>
              Every user's <strong>{FUNCTION_INFO[confirming.functionKey].title}</strong> runs will
              switch to <code>{confirming.model}</code> — including runs already in progress that
              haven't started a call yet. This takes effect immediately and persists across
              restarts.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn" onClick={() => setConfirming(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  const { functionKey, model } = confirming;
                  setConfirming(null);
                  void (async () => {
                    try {
                      await setGlobalAiModel(functionKey, model);
                      showToast(
                        `${FUNCTION_INFO[functionKey].title} now runs on "${model}" for everyone`,
                      );
                      await refresh();
                    } catch (err) {
                      showToast(err instanceof Error ? err.message : 'Failed to apply to everyone');
                    }
                  })();
                }}
              >
                Apply to everyone
              </button>
            </div>
          </Modal>
        )}

        <ToastHost />
      </div>
    </div>
  );
}

function FunctionCard({
  setting,
  onApplyOnlyMe,
  onClearOnlyMe,
  onRequestApplyToEveryone,
  onClearGlobal,
}: {
  setting: AiModelSettingResponse;
  onApplyOnlyMe: (model: string) => void;
  onClearOnlyMe: () => void;
  onRequestApplyToEveryone: (model: string) => void;
  onClearGlobal: () => void;
}) {
  const info = FUNCTION_INFO[setting.functionKey];
  const [model, setModel] = useState(setting.effectiveModel);

  // Re-seed the input whenever the resolved effective model changes (e.g.
  // another admin just changed the shared default).
  useEffect(() => {
    setModel(setting.effectiveModel);
  }, [setting.effectiveModel]);

  const trimmed = model.trim();

  return (
    <div className="admin-card">
      <div className="admin-card-header">
        <h2>{info.title}</h2>
        <span className="admin-card-hint">{info.hint}</span>
      </div>

      <div className="admin-card-status">
        <span>
          Running on <code>{setting.effectiveModel}</code>
          {setting.personalModel !== undefined
            ? ' — your personal test override'
            : setting.globalModel !== undefined
              ? ' — the shared default'
              : ' — server config default'}
        </span>
      </div>

      <label className="tx-field admin-field">
        <span>Model</span>
        {/* The same dropdown as everywhere else, minus the "Default" option:
            this card IS where the default is set. A model that is not on the
            list (a server config default, say) still shows, marked. */}
        <ModelSelect
          functionKey={setting.functionKey}
          kind={info.kind}
          value={model}
          includeDefault={false}
          onChange={setModel}
        />
      </label>

      <div className="admin-card-actions">
        <button
          type="button"
          className="btn"
          disabled={!trimmed}
          onClick={() => onApplyOnlyMe(trimmed)}
        >
          Apply only me
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!trimmed}
          onClick={() => onRequestApplyToEveryone(trimmed)}
        >
          Apply to everyone
        </button>
      </div>

      {(setting.personalModel !== undefined || setting.globalModel !== undefined) && (
        <div className="admin-card-clear-row">
          {setting.personalModel !== undefined && (
            <button type="button" className="btn btn-ghost" onClick={onClearOnlyMe}>
              Stop testing (use the shared default)
            </button>
          )}
          {setting.globalModel !== undefined && (
            <button type="button" className="btn btn-ghost" onClick={onClearGlobal}>
              Clear shared default
            </button>
          )}
        </div>
      )}
    </div>
  );
}
